import React, { useState } from 'react';
import { ChevronDown, User, LogOut, Plus, DollarSign, UserPlus, QrCode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useCurrentCompany, switchCompany } from '../../hooks/useCurrentCompany';
import { useAuth } from '../../constants/providers/AuthProvider';
import { supabase } from '../../lib/supabase';
import { NotificationBell } from '../notifications/NotificationBell';

interface TopBarV2Props {
  /** Callback when + Payment hot button is clicked (mobile/tablet only) */
  onAddPayment?: () => void;
  /** Callback when + Student hot button is clicked (mobile/tablet only) */
  onAddStudent?: () => void;
  /** Callback when + QR hot button is clicked (mobile/tablet only) */
  onAddQR?: () => void;
}

/**
 * TopBarV2 - New top bar following the prototype design.
 *
 * Always visible (desktop + mobile). Contains:
 * - Spacer for sidebar on desktop (sidebar overlays this area)
 * - Workspace (company) switcher
 * - Quick actions (+) button on mobile/tablet (below md)
 * - Notification bell
 * - User avatar with sign-out
 */
export function TopBarV2({ onAddPayment, onAddStudent, onAddQR }: TopBarV2Props = {}) {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const { company, allCompanies, isLoading } = useCurrentCompany();

  const [gymDropdownOpen, setGymDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);

  const hasQuickActions = onAddPayment || onAddStudent || onAddQR;

  const handleSwitchCompany = async (companyId: string) => {
    try {
      await switchCompany(companyId);
      setGymDropdownOpen(false);
      await refreshSession();
      window.location.reload();
    } catch (err) {
      console.error('Error switching company:', err);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <div className="fixed top-0 left-0 right-0 h-16 bg-[#1a1818] border-b border-white/10 z-50">
      <div className="h-full flex items-center justify-between">
        {/* Left Section - Sidebar spacer + Workspace Switcher */}
        <div className="flex items-center min-w-0">
          {/* Spacer for sidebar on desktop - sidebar overlays this area; w-12 on mobile for padding */}
          <div className="w-12 md:w-20 shrink-0" />

          {/* Workspace Switcher */}
          {!isLoading && company && (
            <div className="relative ml-2 md:ml-4 min-w-0">
              <button
                onClick={() => setGymDropdownOpen(!gymDropdownOpen)}
                className="flex items-center gap-2 md:gap-3 px-2 md:px-3 py-2 rounded-lg hover:bg-white/5 transition-colors min-w-0"
              >
                {/* Company Logo */}
                <div className="w-8 h-8 rounded-full bg-rollops-primary flex items-center justify-center text-text-primary font-bold text-sm shrink-0">
                  {company.logo_url ? (
                    <img src={company.logo_url} alt={company.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    company.name.charAt(0).toUpperCase()
                  )}
                </div>

                {/* Company Name - Hidden on tiny screens, truncates on small */}
                <span className="text-text-primary font-medium text-sm md:text-base truncate hidden sm:inline max-w-[120px] sm:max-w-[200px] md:max-w-none">
                  {company.name}
                </span>

                {/* Chevron */}
                <ChevronDown className={`w-4 h-4 text-text-tertiary transition-transform shrink-0 ${gymDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Company Switcher Dropdown */}
              {gymDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setGymDropdownOpen(false)}
                  />
                  <div className="absolute top-full left-0 mt-2 w-64 bg-[#221f1f] border border-white/10 rounded-lg shadow-lg z-50 py-2 max-h-80 overflow-y-auto scrollbar-styled">
                    {allCompanies.map((item) => (
                      <button
                        key={item.company_id}
                        onClick={() => handleSwitchCompany(item.company_id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left ${
                          company.id === item.company_id ? 'bg-white/5' : ''
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full bg-rollops-primary flex items-center justify-center text-text-primary font-bold text-sm shrink-0">
                          {item.company.logo_url ? (
                            <img src={item.company.logo_url} alt={item.company.name} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            item.company.name.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-text-primary font-medium truncate block">{item.company.name}</span>
                          <span className="text-text-tertiary text-xs truncate block">{item.role}</span>
                        </div>
                        {company.id === item.company_id && (
                          <div className="w-2 h-2 rounded-full bg-rollops-primary shrink-0" />
                        )}
                      </button>
                    ))}

                    {/* Create New Gym */}
                    <div className="border-t border-white/10 mt-2 pt-2">
                      <span
                        className="w-full block px-4 py-3 text-left text-text-tertiary font-medium opacity-60 cursor-not-allowed"
                        aria-disabled="true"
                      >
                        + Create New Gym
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Middle Section - Reserved for Global Search (hidden on mobile) */}
        <div className="hidden lg:flex flex-1 max-w-2xl mx-8">
          <div className="h-10 w-full rounded-lg border border-white/10 bg-white/5 flex items-center px-4">
            <span className="text-text-muted text-sm italic">Global search (coming soon)</span>
          </div>
        </div>

        {/* Spacer for mobile when search is hidden */}
        <div className="flex-1 lg:hidden" />

        {/* Right Section - Utilities (never squish) */}
        <div className="flex items-center gap-2 md:gap-4 pr-3 md:pr-4 shrink-0">
          {/* Quick Actions (+) - Mobile/Tablet only (below md where sidebar is hidden) */}
          {hasQuickActions && (
            <div className="relative md:hidden">
              <button
                onClick={() => setQuickActionsOpen(!quickActionsOpen)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${
                  quickActionsOpen
                    ? 'border-white/30 bg-white/10 text-[#EBFFEE]'
                    : 'border-white/20 text-[#A9A3A3] hover:bg-white/5 hover:text-[#EBFFEE]'
                }`}
                aria-label="Quick actions"
              >
                <Plus className={`w-5 h-5 transition-transform ${quickActionsOpen ? 'rotate-45' : ''}`} />
              </button>

              {/* Vertical dropdown */}
              {quickActionsOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setQuickActionsOpen(false)}
                  />
                  <div className="absolute top-full right-0 mt-2 w-52 bg-[#221f1f] border border-white/10 rounded-lg shadow-lg z-50 py-1">
                    <button
                      disabled
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-[#A9A3A3] opacity-50 cursor-not-allowed"
                    >
                      <DollarSign className="w-4 h-4" />
                      <span className="text-sm">Add Payment</span>
                    </button>
                    {onAddStudent && (
                      <button
                        onClick={() => {
                          setQuickActionsOpen(false);
                          onAddStudent();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left text-[#EBFFEE]"
                      >
                        <UserPlus className="w-4 h-4" />
                        <span className="text-sm">Add Student</span>
                      </button>
                    )}
                    {onAddQR && (
                      <button
                        onClick={() => {
                          setQuickActionsOpen(false);
                          onAddQR();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left text-[#EBFFEE]"
                      >
                        <QrCode className="w-4 h-4" />
                        <span className="text-sm">Add QR Code</span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Notification Bell - Uses real notification system */}
          <NotificationBell />

          {/* User Avatar Dropdown */}
          <div className="relative">
            <button
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              className="p-1 rounded-full hover:ring-2 hover:ring-white/20 transition-all"
            >
              <div className="w-9 h-9 rounded-full bg-[#2a2727] flex items-center justify-center">
                <User className="w-5 h-5 text-text-secondary" />
              </div>
            </button>

            {/* User Menu Dropdown */}
            {userDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setUserDropdownOpen(false)}
                />
                <div className="absolute top-full right-0 mt-2 w-56 bg-[#221f1f] border border-white/10 rounded-lg shadow-lg z-50 py-2">
                  <button
                    onClick={() => {
                      setUserDropdownOpen(false);
                      navigate('/account-settings');
                    }}
                    className="w-full px-4 py-3 hover:bg-white/5 transition-colors text-left text-text-primary"
                  >
                    Account Settings
                  </button>
                  <button
                    onClick={() => {
                      setUserDropdownOpen(false);
                      window.open('mailto:support@rollops.pro', '_blank');
                    }}
                    className="w-full px-4 py-3 hover:bg-white/5 transition-colors text-left text-text-primary"
                  >
                    Help & Support
                  </button>
                  <div className="border-t border-white/10 mt-2 pt-2">
                    <button
                      onClick={handleSignOut}
                      className="w-full px-4 py-3 hover:bg-white/5 transition-colors text-left text-rollops-primary font-medium flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
